import React, {useEffect, useState} from 'react';
import {animated, useSpring} from '@react-spring/web';
import axios from 'axios';
import {URLS} from '../config';
import {custom} from '../custom';
import {svg} from '../assets/svg';
import {theme} from '../constants';
import {actions} from '../store/actions';
import {hooks, RootState} from '../hooks';
import {items} from '../items';

const sortingBy = [
  {id: 1, title: 'فروش'},
  {id: 2, title: 'پرفروش‌ترین'},
  {id: 3, title: 'جدیدترین'},
  {id: 4, title: 'قیمت: از کم به زیاد'},
  {id: 5, title: 'قیمت: از زیاد به کم'},
];

export const Brands: React.FC = () => {
  const dispatch = hooks.useAppDispatch();
  const navigate = hooks.useAppNavigate();
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [brandData, setBrandData] = useState<any[]>([]);
  const [sort, setSort] = useState(sortingBy[0]);
  const [modalIsOpen, setIsOpen] = useState(false);

  const limit = 10;
  const token = hooks.useAppSelector(state => state.auth.token);

  const cart = hooks.useAppSelector((state: RootState) => state.cartSlice.list);
  const total = hooks.useAppSelector((state: RootState) => state.cartSlice.total);

  const getData = async () => {
    if (!hasMore || loading) return;

    setLoading(true);
    try {
      const response = await axios.get(`${URLS.GET_BRANDS}?limit=${limit}&offset=${offset}`, {
        headers: {
          Authorization: `JWT ${token}`,
        },
      });

      const newBrands = response.data.results;
      setBrandData(prev => [...prev, ...newBrands]);

      if (newBrands.length < limit) {
        setHasMore(false);
      } else {
        setOffset(prev => prev + limit);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
    window.scrollTo(0, 0);
  }, []);

  const sortedProducts = [...brandData].sort((a, b) => {
    switch (sort.title) {
      case 'قیمت: از کم به زیاد':
        return a.price - b.price;
      case 'قیمت: از زیاد به کم':
        return b.price - a.price;
      case 'جدیدترین':
        return a.isNew === b.isNew ? 0 : a.isNew ? -1 : 1;
      case 'پرفروش‌ترین':
        return a.isTop === b.isTop ? 0 : a.isTop ? -1 : 1;
      case 'فروش':
        return a.oldPrice === b.oldPrice ? 0 : a.oldPrice ? -1 : 1;
      default:
        return 0;
    }
  });

  const backdropAnimation = useSpring({
    opacity: modalIsOpen ? 1 : 0,
    display: modalIsOpen ? 'flex' : 'none',
  });

  useEffect(() => {
    document.body.style.overflow = modalIsOpen ? 'hidden' : 'auto';
  }, [modalIsOpen]);

  const handleOnClick = () => {
    if (cart.length > 0) {
      dispatch(actions.setScreen('Order'));
      navigate('/home');
    } else {
      alert('سبد خرید شما خالی است');
    }
  };

  const renderHeader = () => (
    <header
      style={{
        top: 0,
        position: 'sticky',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.colors.white,
        zIndex: 4,
      }}
      className="shadow-md"
    >
      <div
        style={{
          height: 52,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <button
          style={{
            position: 'absolute',
            right: 0,
            padding: 20,
            cursor: 'pointer',
            backgroundColor: 'transparent',
            border: 'none',
            rotate: '180deg'
          }}
          onClick={() => navigate(-1)}
        >
          <svg.GoBackSvg />
        </button>
        <h4
          style={{
            width: 'calc(100% - 100px)',
            textAlign: 'center',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            ...theme.fonts.Mulish_600SemiBold,
            fontSize: 18,
            color: theme.colors.mainColor,
          }}
        >
          برندها
        </h4>
        <button
          onClick={handleOnClick}
          style={{
            position: 'absolute',
            left: 0,
            padding: 12,
            borderRadius: 12,
          }}
        >
          <svg.BasketSvg />
          <span
            style={{
              padding: '2px 6px',
              borderRadius: 10,
              left: 35,
              bottom: 12,
              position: 'absolute',
              fontSize: 10,
              color: theme.colors.white,
              ...theme.fonts.Mulish_700Bold,
              backgroundColor: theme.colors.coralRed,
            }}
          >
            <span style={{marginLeft: '5px'}}>{cart.length > 0 ? total.toFixed(2) : 0}</span>
            <span>تومان</span>
          </span>
        </button>
      </div>
    </header>
  );

  const renderContent = () => (
    <div>
      <custom.InfiniteScrollR
        data={brandData}
        next={getData}
        hasMore={hasMore}
        loading={loading}
        endMessage={true}
        style={{paddingBottom: 20}}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            padding: '0 20px',
          }}
        >
          {sortedProducts.map((item: any, index: number) => (
            <items.BrandCardDetail
              key={item.id}
              isLast={index === sortedProducts.length - 1}
              item={item}
              version={1}
            />
          ))}
        </div>
      </custom.InfiniteScrollR>
    </div>
  );

  const renderModal = (): JSX.Element => (
    <animated.div
      className='container'
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(25, 51, 100, 0.6)',
        position: 'fixed',
        inset: 0,
        zIndex: 22,
        overflow: 'hidden',
        justifyContent: 'center',
        alignItems: 'center',
        ...backdropAnimation,
      }}
      onClick={() => setIsOpen(false)}
    >
      <div
        style={{
          width: 'calc(100% - 80px)',
          backgroundColor: 'white',
          borderRadius: 5,
          padding: '10px 20px',
          margin: '0 20px',
        }}
      >
        {sortingBy.map(item => (
          <button
            key={item.id}
            onClick={() => {
              setSort(item);
              setIsOpen(false);
            }}
            style={{
              width: '100%',
              borderBottom: '1px solid #E5E5E5',
              padding: '10px 20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
            }}
          >
            <span style={{fontSize: 16, color: theme.colors.textColor}}>{item.title}</span>
            <div
              style={{
                width: 17,
                height: 17,
                border: '3px solid #E8EFF4',
                borderRadius: '50%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {sort.id === item.id && (
                <div
                  style={{
                    width: 9,
                    height: 9,
                    backgroundColor: theme.colors.mainColor,
                    borderRadius: '50%',
                  }}
                />
              )}
            </div>
          </button>
        ))}
      </div>
    </animated.div>
  );

  return (
    <>
      {renderHeader()}
      {renderContent()}
      {renderModal()}
    </>
  );
};
